import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from 'app/environments/environment';
import { Observable, catchError, firstValueFrom, of, shareReplay, tap } from 'rxjs';
import { EVENT_STATUS } from './event.types';
import {
  FALLBACK_TRANSITIONS,
  NextActionContext,
  NextStatusAction,
  TransitionMatrix,
  canTransition,
  getAllowedTransitions,
  getNextAction,
} from './event-status.transitions';

/**
 * LION-620 (ARCH-01): servicio central del workflow de órdenes.
 * Carga la matriz de transiciones del back una sola vez por sesión
 * (shareReplay), la cachea en un signal para lectura sincrónica desde
 * templates (CD OnPush) y cae al grafo estático (`FALLBACK_TRANSITIONS`)
 * si el GET falla o aún no resolvió. El back sigue siendo la autoridad de
 * validación; acá sólo gobernamos habilitación/presentación.
 */
@Injectable({ providedIn: 'root' })
export class EventWorkflowService {
  private readonly _http = inject(HttpClient);
  private readonly _matrix = signal<TransitionMatrix>(FALLBACK_TRANSITIONS);

  /** Cache con shareReplay: 1 GET por sesión. Fallback al grafo estático si falla. */
  private readonly _load$: Observable<TransitionMatrix> = this._http
    .get<TransitionMatrix>(`${environment.apiURL}events/status-transitions`)
    .pipe(
      catchError(() => of(FALLBACK_TRANSITIONS)),
      tap((m) => this._matrix.set(m)),
      shareReplay(1),
    );

  /** Llamar desde APP_INITIALIZER. */
  preload(): Promise<TransitionMatrix> {
    return firstValueFrom(this._load$);
  }

  /** Lectura sincrónica para helpers (siempre devuelve algo: fallback o cargado). */
  get matrix(): TransitionMatrix {
    return this._matrix();
  }

  getAllowedTransitions(from: EVENT_STATUS): EVENT_STATUS[] {
    return getAllowedTransitions(from, this._matrix());
  }

  canTransition(from: EVENT_STATUS, to: EVENT_STATUS): boolean {
    return canTransition(from, to, this._matrix());
  }

  getNextAction(ctx: NextActionContext): NextStatusAction | null {
    return getNextAction(ctx); // back ya validó; el grafo solo gobierna habilitación
  }
}
